import {flow, Instance, isAlive, types} from "mobx-state-tree";
import {v4} from 'uuid';
import agent from "../agent";

export const Group = types.model({
    id: types.string,
    selectId: types.string,
    name: types.string,
    avatar: types.string,
    allowSendPost: types.boolean,
    allowAddUser: types.boolean,
    isOwner: types.boolean
});

export interface IGroup extends Instance<typeof Group> {
}

export const Image = types
    .model({
        path: types.string,
        isLocalImage: types.boolean,
        retries: types.number,
        isLoaded: types.boolean,

        // filestore props
        url: types.string,
        placeholder: types.string,
        width: types.number,
        height: types.number,
    })
    .views(self => ({
        get isUploaded(): boolean {
            return !!(self.url);
        },
        get getImageItem() {
            let imageType = contentMIMETypeConstants.image.jpeg;
            if (self.url.includes('gif')) {
                imageType = contentMIMETypeConstants.image.gif;
            } else if (self.url.includes('png')) {
                imageType = contentMIMETypeConstants.image.png;
            }
            return {
                key: v4(),
                type: "imageItem",
                imageType: imageType,
                playableDuration: 0,
                sources: {
                    original: {
                        url: self.url,
                        width: self.width,
                        height: self.height,
                        isBase64: false
                    },
                    thumbnail: {
                        url: self.url,
                        width: self.width,
                        height: self.height,
                        isBase64: false
                    },
                    placeholder: {
                        url: self.placeholder,
                        width: self.width,
                        height: self.height,
                        isBase64: true
                    }
                }
            };
        },
    }))
    .volatile(() => ({
        file: null
    }))
    .actions(self => ({
        setFile: function (file: File) {
            // @ts-ignore
            self.file = file;
        },
        reupload: function () {
            self.retries = 0;
            // @ts-ignore
            self.upload();
        },
        setIsLoaded() {
            self.isLoaded = true
        },
        upload: flow(function* () {
            if (!self.path)
                return;
            if (self.retries >= 3)
                return;
            if (!isAlive(self))
                return;
            if (self.url)
                return;

            if (!self.isLocalImage) {
                const {host} = new URL(self.path);
                if (host === 'cloudfront.selectdc.net' || host === 'cdn-sgp.selectdc.net') {
                    self.url = self.path;
                    return;
                }
            }

            try {
                let data;
                if (self.isLocalImage) {
                    // @ts-ignore
                    const resp = yield agent.Filestore.uploadFile(self.file)
                    data = resp.data
                } else {
                    const resp = yield agent.Filestore.uploadUrl(self.path)
                    data = resp.data
                }
                if (!isAlive(self))
                    return;
                self.url = data.url;
                self.placeholder = data.placeholder;
                self.height = data.height;
                self.width = data.width
            } catch (e) {
                self.retries = self.retries + 1;
                if (!isAlive(self))
                    return;
                // @ts-ignore
                self.upload()
            }
        })
    }));

export const ImageDefaultStore = {
    path: '',
    isLocalImage: false,
    isLoaded: false,
    url: '',
    retries: 0,
    placeholder: '',
    width: 0,
    height: 0
};

export const contentMIMETypeConstants = {
    text: {
        plain: 'text/plain',
        html: 'text/html'
    },
    image: {
        jpeg: 'image/jpeg',
        png: 'image/png',
        webp: 'image/webp',
        gif: 'image/gif'
    },
    audio: {
        mpeg: 'audio/mpeg',
        midi: 'audio/midi',
        webm: 'audio/webm',
        ogg: 'audio/ogg',
        wav: 'audio/wav'
    },
    video: {
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        webm: 'video/webm',
        ogg: 'video/ogg'
    },
    application: {
        pdf: 'application/pdf',
        json: 'application/json'
    }
};

export interface IImage extends Instance<typeof Image> {
}
